import "./FirmwareHistoryView.scss";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../Button";
import { EmptyPlaceholder } from "../EmptyPlaceholder";
import { Iconfont } from "../Iconfont";
import {
  formatFileSize,
  normalizeReleaseNotes,
} from "../FirmwareUpdateView/FirmwareUpdateView";

export type FirmwareHistoryItem = {
  version: string;
  releaseDate?: string;
  /** 字节 number，或 "776.8KB" 这类可读字符串 */
  size?: number | string | null;
  /** 单条字符串或字符串数组均可（与星闪字段名一致） */
  releaseNotes?: string | string[];
  fileName?: string;
  toolFileName?: string;
  [key: string]: unknown;
};

export interface FirmwareHistoryViewProps {
  activeTab: number;
  onTabChange: (tab: number) => void;
  availableFirmwares: FirmwareHistoryItem[];
  currentVersion: string;
  /** 无固件跳转本地更新后禁用固件库 Tab（不置灰，仅禁用点击 + 禁用光标） */
  firmwareLibraryDisabled?: boolean;
  onDownload: (firmware: FirmwareHistoryItem) => void;
  onInstall: (firmware: FirmwareHistoryItem) => void;
  onManualFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** 预留：更新恢复 Tab（当前未启用） */
  recoveryActions?: {
    onExitBootloader: () => void;
    onReconnect: () => void;
    onReauthorize: () => void;
    onForceReset: () => void;
  };
}

function normalizeVersionKey(v: string): string {
  return String(v || "")
    .trim()
    .replace(/^[Vv][.\s]*/, "")
    .trim()
    .toUpperCase();
}

function firmwareNote(firmware: FirmwareHistoryItem): string {
  return normalizeReleaseNotes(firmware.releaseNotes)[0] ?? "";
}

export const FirmwareHistoryView: React.FC<FirmwareHistoryViewProps> = ({
  activeTab,
  onTabChange,
  availableFirmwares,
  currentVersion,
  firmwareLibraryDisabled = false,
  onDownload,
  onInstall,
  onManualFileSelect,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentKey = normalizeVersionKey(currentVersion);

  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="history-modal">
      <div className="tabs-box">
        <div className="tabs">
          <Button
            className={`tab ${activeTab === 0 ? "active" : ""} ${
              firmwareLibraryDisabled ? "tab-disabled" : ""
            }`}
            variant="secondary"
            onClick={() => {
              if (firmwareLibraryDisabled) return;
              onTabChange(0);
            }}
            aria-disabled={firmwareLibraryDisabled || undefined}
          >
            {t("keyboard.other.firmwareLibrary")}
          </Button>
          <Button
            className={`tab ${activeTab === 1 ? "active" : ""}`}
            variant="secondary"
            onClick={() => onTabChange(1)}
          >
            {t("keyboard.other.localUpdate")}
          </Button>
        </div>
      </div>

      <div className="tab-content">
        {activeTab === 0 && (
          <div className="firmware-list">
            {availableFirmwares.length === 0 && (
              <EmptyPlaceholder
                className="firmware-list-empty"
                text={t("keyboard.other.noFirmware")}
              />
            )}
            {availableFirmwares.map((firmware, index) => {
              const note = firmwareNote(firmware);
              const isCurrent =
                !!currentKey &&
                normalizeVersionKey(firmware.version) === currentKey;
              return (
                <React.Fragment key={`${firmware.version}-${index}`}>
                  <div className="firmware-item">
                    <div className="firmware-info">
                      <div className="firmware-version">
                        {t("keyboard.other.version")} {firmware.version}
                        {index === 0 && (
                          <span className="latest-badge">
                            {t("keyboard.other.latest")}
                          </span>
                        )}
                        {isCurrent && (
                          <span className="current-badge">
                            {t("keyboard.other.current")}
                          </span>
                        )}
                      </div>
                      <div className="firmware-meta">
                        {firmware.releaseDate ? (
                          <span>{firmware.releaseDate}</span>
                        ) : null}
                        <span>{formatFileSize(firmware.size)}</span>
                      </div>
                      {note ? (
                        <div className="firmware-notes">{note}</div>
                      ) : null}
                    </div>
                    <div className="firmware-actions">
                      <Button
                        className="download-btn"
                        variant="secondary"
                        onClick={() => onDownload(firmware)}
                        title={t("keyboard.button.download")}
                      >
                        {t("keyboard.button.download")}
                      </Button>
                      <Button
                        className="install-btn"
                        variant="primary"
                        onClick={() => onInstall(firmware)}
                      >
                        {isCurrent
                          ? t("keyboard.button.reinstall")
                          : t("keyboard.button.install")}
                      </Button>
                    </div>
                  </div>
                  {index !== availableFirmwares.length - 1 && (
                    <div className="firmware-divider" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {activeTab === 1 && (
          <div className="local-update">
            <div className="local-update-content">
              <div className="local-update-box">
                <div className="icon" />
                <div className="title">
                  {t("keyboard.other.selectLocalFirmware")}
                </div>
                <div className="description">
                  {t("keyboard.other.selectBinFile")}
                </div>
                <Button
                  className="select-file-btn"
                  variant="primary"
                  onClick={handleSelectFileClick}
                >
                  {t("keyboard.button.selectFile")}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".bin"
                  onChange={onManualFileSelect}
                  style={{ display: "none" }}
                />
              </div>
              <div className="local-update-tips">
                <div className="tips-icon">
                  <Iconfont icon="warning" />
                </div>
                <div className="tips-text">
                  {t("keyboard.other.localUpdateTips")}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
